import { useSyncExternalStore } from 'react';
import { planNotifications, snooze as snoozeCore, Temporal, type NotificationPlan, type PlannedNotification, type SnoozeRecord } from '@autoday/core';
import type { AppStore } from '../store/appStore.ts';
import type { AuthStore } from '../auth/authStore.ts';
import type { ProfileMeta } from '../profile/registry.ts';
import { platform, randomId } from '../platform.ts';
import { CapacitorLocalTransport, clearAccountNotifications, notificationTag } from './capacitorLocal.ts';
import { NoBackgroundTransport, WebPushTransport } from './webPush.ts';
import type { ChannelStatus, NotificationTransport, ScheduleReport } from './types.ts';
import { currentLang, getLang } from '../i18n/i18n.ts';

export const HORIZON_DAYS = 60;

export interface NotifSnapshot {
  status: ChannelStatus | null;
  report: ScheduleReport | null;
  plan: NotificationPlan | null;
  busy: boolean;
  lastError: string | null;
  /** Medan appen är öppen (webb utan bakgrund): förfallna påminnelser att visa i appen. */
  inApp: PlannedNotification[];
}

/**
 * Binder NotificationPlanner till rätt transport. Planen beräknas om från BESTÄNDIG data efter varje
 * sparad ändring, vid appstart, vid återkomst till förgrunden och vid tidszonsändring – aldrig från
 * enbart minnet i en webview.
 */
export class NotificationService {
  private snap: NotifSnapshot = { status: null, report: null, plan: null, busy: false, lastError: null, inApp: [] };
  private listeners = new Set<() => void>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private fgTimer: ReturnType<typeof setInterval> | null = null;
  transport: NotificationTransport;

  /** Profilens notismarkering: 'guest' eller kontots opaka markering. */
  readonly accountTag: string;

  constructor(
    private store: AppStore,
    private auth: AuthStore,
  ) {
    this.accountTag = auth.profile.kind === 'account' ? (auth.profile.accountTag ?? 'unbound') : 'guest';
    this.transport = this.pickTransport();
    store.onCommitted.push(() => this.scheduleReplan());
    // Språkbyte: samma påminnelser och tider, ny text på lokala notiser och i statusen.
    getLang().subscribe(() => this.scheduleReplan());
    auth.beforeSignOut.push((p) => this.disconnectForLogout(p));
  }

  private pickTransport(): NotificationTransport {
    const p = platform();
    if (p === 'android' || p === 'ios') return new CapacitorLocalTransport(p, this.accountTag);
    // Web Push kräver en kontoprofil (servern planerar). Inloggning betyder INTE att push är godkänt:
    // behörighet begärs bara när användaren själv väljer det i Notisstatus.
    if (this.auth.profile.kind === 'account') return new WebPushTransport(this.store.device.deviceId);
    return new NoBackgroundTransport();
  }

  /**
   * Utloggning: koppla bort DENNA enhets leveranskanal för kontot och ta bort kontots synliga notiser.
   * Serverns prenumeration tas bort av AuthStore (kräver sessionen). Andra enheter och gästens larm
   * påverkas inte.
   */
  async disconnectForLogout(profile: ProfileMeta): Promise<void> {
    if (this.fgTimer) clearInterval(this.fgTimer);
    const tag = profile.accountTag ?? this.accountTag;
    if (platform() === 'web') {
      const reg = 'serviceWorker' in navigator ? await navigator.serviceWorker.getRegistration().catch(() => undefined) : undefined;
      if (reg) {
        for (const n of await reg.getNotifications().catch(() => [] as Notification[])) {
          const d = (n.data ?? {}) as { accountTag?: string };
          if (!d.accountTag || d.accountTag === tag) n.close();
        }
        const sub = await reg.pushManager.getSubscription().catch(() => null);
        await sub?.unsubscribe().catch(() => undefined);
      }
      return;
    }
    // Både den bundna markeringen och den som gällde vid sidstart ('unbound' för en äldre profil).
    await clearAccountNotifications([...new Set([tag, this.accountTag])]);
  }

  get snapshot() {
    return this.snap;
  }
  subscribe = (l: () => void) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };
  private emit(p: Partial<NotifSnapshot>) {
    this.snap = { ...this.snap, ...p };
    for (const l of this.listeners) l();
  }

  async start(): Promise<void> {
    if (platform() !== 'web') await this.attachNativeListeners();
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void this.replan();
    });
    // Förgrundskontroll (endast för att visa missade/förfallna i appen – inte en bakgrundslösning).
    this.fgTimer = setInterval(() => this.checkForeground(), 30_000);
    await this.replan();
  }

  stop() {
    if (this.fgTimer) clearInterval(this.fgTimer);
  }

  scheduleReplan() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.replan(), 400);
  }

  async replan(): Promise<void> {
    this.emit({ busy: true });
    try {
      const state = this.store.state;
      const dev = this.store.device;
      const now = this.store.clock.now();
      const status = await this.transport.status();
      const plan = planNotifications(state, {
        now,
        horizonDays: HORIZON_DAYS,
        capacity: this.transport.capacity,
        snoozes: dev.snoozes,
        delivered: new Set(Object.keys(dev.delivered)),
        lastPlannedAt: dev.lastPlannedAt ? Temporal.Instant.from(dev.lastPlannedAt) : null,
        lang: currentLang(),
      });
      const report = await this.transport.apply(plan, { hideSensitive: state.settings.hideSensitiveOnLockScreen });
      await this.store.updateDevice((d) => ({
        ...d,
        lastPlannedAt: now.toString(),
        lastPlan: plan,
        snoozes: activeSnoozes(d.snoozes, plan, now.toString()),
        observedErrors: report.errors.length ? [...d.observedErrors, ...report.errors.map((m) => ({ at: now.toString(), channel: report.channel, message: m }))].slice(-30) : d.observedErrors,
      }));
      this.emit({ status, report, plan, busy: false, lastError: null });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      await this.store.updateDevice((d) => ({ ...d, observedErrors: [...d.observedErrors, { at: new Date().toISOString(), channel: this.transport.channel, message: msg }].slice(-30) })).catch(() => undefined);
      this.emit({ busy: false, lastError: msg });
    }
  }

  async requestPermission() {
    const status = await this.transport.requestPermission();
    this.emit({ status });
    await this.replan();
  }

  async snooze(pointId: string, minutes: number, title: string, open: PlannedNotification['open'], sensitive: boolean) {
    const now = this.store.clock.now();
    const res = snoozeCore(this.store.device.snoozes, { pointId, title, ref: open, sensitive }, minutes, now, this.store.state.settings.maxSnoozesPerPoint);
    if (!res.ok) throw new Error(res.reason);
    await this.store.updateDevice((d) => ({ ...d, snoozes: res.snoozes }));
    await this.replan();
  }

  async markDelivered(ids: string[]) {
    const now = new Date().toISOString();
    await this.store.updateDevice((d) => ({ ...d, delivered: { ...pruneDelivered(d.delivered), ...Object.fromEntries(ids.map((i) => [i, now])) } }));
  }

  async runTest(): Promise<void> {
    const testId = randomId('t_');
    const fireAt = new Date(Date.now() + 60_000);
    const permissionAtTest = this.snap.status?.permission ?? (await this.transport.status().catch(() => null))?.permission ?? 'unknown';
    try {
      const r = await this.transport.scheduleTest(fireAt, testId);
      await this.store.updateDevice((d) => ({
        ...d,
        notificationTest: { id: testId, channel: this.transport.channel, scheduledAt: new Date().toISOString(), fireAt: fireAt.toISOString(), result: r.ok ? 'scheduled' : 'failed', detail: r.detail, permissionAtTest },
      }));
    } catch (e) {
      await this.store.updateDevice((d) => ({
        ...d,
        notificationTest: { id: testId, channel: this.transport.channel, scheduledAt: new Date().toISOString(), fireAt: fireAt.toISOString(), result: 'failed', detail: e instanceof Error ? e.message : String(e), permissionAtTest },
      }));
    }
  }

  async confirmTest(seen: boolean) {
    await this.store.updateDevice((d) => (d.notificationTest ? { ...d, notificationTest: { ...d.notificationTest, result: seen ? 'confirmed' : 'not-seen', confirmedAt: new Date().toISOString() } } : d));
  }

  private checkForeground() {
    const plan = this.snap.plan;
    // Endast lokal webbprofil utan bakgrundskanal – annars skulle samma enhet få dubbla påminnelser.
    if (!plan || this.transport.channel !== 'none') return;
    const now = new Date().toISOString();
    const delivered = this.store.device.delivered;
    const due = plan.scheduled.filter((n) => n.fireAt <= now && !n.pointIds.every((p) => delivered[p]));
    if (!due.length) return;
    this.emit({ inApp: [...this.snap.inApp, ...due.filter((d) => !this.snap.inApp.some((x) => x.id === d.id))] });
    if (this.transport.channel === 'none' && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      for (const n of due) new Notification(n.publicTitle, { body: n.publicBody, tag: n.id, icon: '/icons/autoday-192.png' });
    }
    void this.markDelivered(due.flatMap((d) => d.pointIds));
  }

  dismissInApp(id: string) {
    this.emit({ inApp: this.snap.inApp.filter((n) => n.id !== id) });
  }

  private async attachNativeListeners() {
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    await LocalNotifications.addListener('localNotificationActionPerformed', async (a) => {
      const extra = (a.notification.extra ?? {}) as { autodayId?: string; pointIds?: string[]; open?: PlannedNotification['open']; kind?: string; accountTag?: string };
      // En notis från en annan lokal profil (t.ex. utloggat konto) eller utan markering öppnar aldrig
      // data, snoozas inte och kvitteras inte i denna profil.
      if (extra.accountTag !== this.accountTag) {
        window.location.hash = '#/idag';
        return;
      }
      if (extra.kind === 'test') {
        window.location.hash = '#/notiser';
        return;
      }
      if (extra.pointIds) await this.markDelivered(extra.pointIds);
      if (a.actionId === 'snooze10' || a.actionId === 'snooze60') {
        const minutes = a.actionId === 'snooze10' ? 10 : 60;
        const pid = extra.pointIds?.[0];
        if (pid && extra.open) await this.snooze(pid, minutes, a.notification.title ?? (currentLang() === 'en' ? 'Reminder' : 'Påminnelse'), extra.open, true).catch(() => undefined);
        return;
      }
      // Tryck/Öppna → visa posten. Att svepa bort markerar inget som klart (ingen lyssnare för det).
      if (extra.open) window.location.hash = `#/post/${encodeURIComponent(extra.open.kind === 'local' ? extra.open.id : extra.open.key)}`;
    });
    await LocalNotifications.addListener('localNotificationReceived', async (n) => {
      if (notificationTag(n) !== this.accountTag) return; // kvittenser hålls per profil
      const extra = (n.extra ?? {}) as { pointIds?: string[] };
      if (extra.pointIds) await this.markDelivered(extra.pointIds);
    });
    // Levererade notiser sedan senast (appen var stängd).
    try {
      const d = await LocalNotifications.getDeliveredNotifications();
      const ids = d.notifications.filter((n) => notificationTag(n) === this.accountTag).flatMap((n) => ((n.extra ?? {}) as { pointIds?: string[] }).pointIds ?? []);
      if (ids.length) await this.markDelivered(ids);
    } catch {
      /* stöds inte på alla versioner */
    }
  }
}

/**
 * Snoozer som fortfarande väntar: framtida, eller passerade men uppskjutna av tysta tider (då finns de
 * kvar i planen med en senare tid). Tidigare togs de bort när ursprungstiden passerat – en snooze in i
 * tysta tider försvann därför innan den visats.
 */
export function activeSnoozes(snoozes: SnoozeRecord[], plan: Pick<NotificationPlan, 'scheduled'>, nowIso: string): SnoozeRecord[] {
  const planned = new Set(plan.scheduled.filter((n) => n.kind === 'snooze').map((n) => n.id));
  return snoozes.filter((s) => s.fireAt > nowIso || planned.has(`${s.pointId}:s${s.count}`));
}

function pruneDelivered(d: Record<string, string>): Record<string, string> {
  const entries = Object.entries(d);
  if (entries.length < 3000) return d;
  return Object.fromEntries(entries.sort((a, b) => b[1].localeCompare(a[1])).slice(0, 2000));
}

let svc: NotificationService | null = null;
export function setNotificationService(s: NotificationService) {
  svc = s;
}
export function getNotificationService(): NotificationService {
  if (!svc) throw new Error('Notistjänst ej initierad');
  return svc;
}
export function useNotifications(): NotifSnapshot {
  const s = getNotificationService();
  return useSyncExternalStore(s.subscribe, () => s.snapshot);
}
